import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import { useCreateRole, useUpdateRole, useApplications } from 'hooks/api';
import { roleService } from 'api/services';
import { lookupService, type StatusLookup } from 'api/services/lookupService';
import type { CreateRoleRequest, UpdateRoleRequest, RoleResponse } from 'api/types';
import { ROLE_TYPES } from 'api/types/roles.types';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ============================================================================
// TYPES
// ============================================================================

interface RoleFormModalProps {
  isOpen: boolean;
  mode?: 'create' | 'edit';
  role?: RoleResponse | null;
  onClose: () => void;
  onSaveSuccess: () => void;
  tabId?: string;
  moduleName?: string;
  onMinimize?: () => void;
  onRestoreModal?: (restoredMode: 'create' | 'edit', instanceId?: string, roleCode?: string) => void;
  createInstanceId?: string;
}

interface RoleFormData {
  code: string;
  name: string;
  roleType: string;
  appId: string;
  description: string;
  composite: boolean;
  compositeRoles: string;
  attributes: string;
  realmName: string;
  clientId: string;
  /** Status code from iam.tbl_status (type = ROLE). Edit mode only. */
  statusCode: string;
}

/** Sent as appId when the user picks "None" — the API reads it as "clear". */
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const INITIAL_FORM_DATA: RoleFormData = {
  code: '',
  name: '',
  roleType: 'custom',
  appId: '',
  description: '',
  composite: false,
  compositeRoles: '',
  attributes: '',
  realmName: '',
  clientId: '',
  statusCode: '',
};

// ============================================================================
// COMPONENT
// ============================================================================

const RoleFormModal: React.FC<RoleFormModalProps> = ({
  isOpen,
  mode = 'create',
  role,
  onClose,
  onSaveSuccess,
  tabId,
  moduleName,
  onMinimize,
  onRestoreModal,
  createInstanceId,
}) => {
  const isEdit = mode === 'edit';
  const { createRole, loading: creating } = useCreateRole();
  const { updateRole, loading: updating } = useUpdateRole();
  const [checkingCode, setCheckingCode] = useState(false);
  const saving = creating || updating;
  const { minimize } = useModalMinimize();

  // Applications drive the optional "Application" FK dropdown
  const { data: applications = [] } = useApplications();

  const [formData, setFormData] = useState<RoleFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef<RoleFormData>(INITIAL_FORM_DATA);
  formDataRef.current = formData;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // ---- Status options: loaded from iam.tbl_status where type = 'ROLE' (edit mode only) ----
  const [statusOptions, setStatusOptions] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    if (!isEdit || !isOpen) return;
    let cancelled = false;
    lookupService.getStatuses('ROLE').then((res) => {
      if (cancelled) return;
      const options = ((res.data as any)?.data || res.data || []) as StatusLookup[];
      setStatusOptions(options.map((s) => ({ value: s.code, label: s.name })));
    }).catch(() => { /* silent — the field simply stays empty */ });
    return () => { cancelled = true; };
  }, [isEdit, isOpen]);

  /**
   * Roles created before the ROLE_* statuses existed point at legacy status rows
   * (the generic "active" sentinel or APP_ACTIVE). Once the ROLE options load,
   * map such a role onto the matching ROLE_* option so the dropdown shows its
   * real state instead of appearing blank.
   */
  useEffect(() => {
    if (!isEdit || statusOptions.length === 0) return;
    setFormData(prev => {
      if (!prev.statusCode || statusOptions.some(o => o.value === prev.statusCode)) return prev;
      const isInactive = prev.statusCode.toLowerCase().includes('inactive');
      const match = statusOptions.find(o =>
        isInactive
          ? o.value.toLowerCase().includes('inactive')
          : o.value.toLowerCase().includes('active') && !o.value.toLowerCase().includes('inactive'),
      );
      return match ? { ...prev, statusCode: match.value } : prev;
    });
  }, [isEdit, statusOptions]);

  // ---- Populate on open (Pattern B: modal owns its form data) ----
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    if (isEdit && role) {
      const editData: RoleFormData = {
        code: role.code || '',
        name: role.name || '',
        roleType: role.roleType || '',
        appId: role.appId ? String(role.appId) : '',
        description: role.description || '',
        composite: !!role.isComposite,
        compositeRoles: '',
        attributes:
          role.attributes && Object.keys(role.attributes).length > 0
            ? JSON.stringify(role.attributes, null, 2)
            : '',
        realmName: '',
        clientId: '',
        statusCode: role.statusCode || '',
      };
      setFormData(editData);
      initialSnapshotRef.current = JSON.stringify(editData);
    } else {
      setFormData({ ...INITIAL_FORM_DATA });
      initialSnapshotRef.current = JSON.stringify(INITIAL_FORM_DATA);
    }
    setFieldErrors({});
  }, [isOpen, isEdit, role]);

  const getFieldError = useCallback((field: string) => fieldErrors[field] || '', [fieldErrors]);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleFieldChange = useCallback((field: keyof RoleFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    clearFieldError(field);
  }, [clearFieldError]);

  /**
   * Selecting an application fills in its Keycloak coordinates:
   *   Client ID   = the application code (the platform registers each
   *                 application in Keycloak under its code)
   *   Realm Name  = effectiveRealmName resolved by the API (the app's own realm
   *                 when dedicated, otherwise the platform shared realm)
   * Both stay editable afterwards; clearing the application clears them.
   */
  const handleApplicationChange = useCallback((appId: string) => {
    const app = applications.find(a => String(a.appId) === appId);
    setFormData(prev => ({
      ...prev,
      appId,
      clientId: app ? app.appCode || '' : '',
      realmName: app ? app.effectiveRealmName || app.keycloakRealmName || '' : '',
    }));
    clearFieldError('appId');
    clearFieldError('clientId');
    clearFieldError('realmName');
  }, [applications, clearFieldError]);

  // ---- Application options ----
  const applicationOptions = useMemo(
    () => [
      { value: '', label: 'None (not application-scoped)' },
      ...applications.map(a => ({
        value: String(a.appId),
        label: `${a.appCode} — ${a.appName}`,
      })),
    ],
    [applications],
  );

  const roleTypeOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = ROLE_TYPES.map(t => ({
      value: t as string,
      label: t.charAt(0).toUpperCase() + t.slice(1),
    }));
    // Edit mode is read-only for role type, but an existing record may carry a
    // value outside the canonical list — keep it selectable so it still renders.
    if (formData.roleType && !ROLE_TYPES.some(t => t === formData.roleType)) {
      options.push({ value: formData.roleType, label: formData.roleType });
    }
    return options;
  }, [formData.roleType]);

  // ---- Validation (mirrors CreateRoleRequest / UpdateRoleRequest constraints) ----
  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) errors.name = 'Role name is required';
    else if (formData.name.trim().length < 2) errors.name = 'Minimum 2 characters';
    else if (formData.name.length > 50) errors.name = 'Maximum 50 characters';

    if (formData.description && formData.description.length > 200) {
      errors.description = 'Maximum 200 characters';
    }

    if (!isEdit) {
      if (!formData.code.trim()) errors.code = 'Role code is required';
      else if (formData.code.trim().length < 2) errors.code = 'Minimum 2 characters';
      else if (formData.code.length > 100) errors.code = 'Maximum 100 characters';
      else if (!/^[a-zA-Z0-9_-]+$/.test(formData.code.trim())) {
        errors.code = 'Only letters, numbers, hyphens and underscores';
      }

      if (!formData.roleType) errors.roleType = 'Role type is required';

      // The role is created in Keycloak as a CLIENT role, which needs both
      if (!formData.realmName.trim()) errors.realmName = 'Realm name is required';
      if (!formData.clientId.trim()) errors.clientId = 'Client ID is required';
    }

    if (formData.attributes.trim()) {
      try {
        const parsed = JSON.parse(formData.attributes);
        if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
          errors.attributes = 'Attributes must be a JSON object';
        }
      } catch {
        errors.attributes = 'Invalid JSON';
      }
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.querySelector<HTMLElement>('input, textarea')?.focus();
      });
      return false;
    }
    return true;
  }, [formData, isEdit]);

  // ---- Save ----
  const handleSaveClick = useCallback(async () => {
    if (!validate()) return;

    // Create only: reject a duplicate code up-front via GET /Role/code/{code}
    if (!isEdit) {
      setCheckingCode(true);
      try {
        const existing = await roleService.getByCode(formData.code.trim());
        if (existing.success && existing.data) {
          setFieldErrors({ code: `Role code "${formData.code.trim()}" already exists` });
          setCheckingCode(false);
          return;
        }
      } catch {
        // 404 = code is free; any other failure is surfaced by the save call itself
      }
      setCheckingCode(false);
    }

    setConfirmSaveOpen(true);
  }, [validate, isEdit, formData.code]);

  const parseAttributes = useCallback((): Record<string, unknown> | undefined => {
    if (!formData.attributes.trim()) return undefined;
    try {
      return JSON.parse(formData.attributes);
    } catch {
      return undefined;
    }
  }, [formData.attributes]);

  const parseCompositeRoles = useCallback((): string[] | undefined => {
    const list = formData.compositeRoles
      .split(',')
      .map(r => r.trim())
      .filter(Boolean);
    return list.length > 0 ? list : undefined;
  }, [formData.compositeRoles]);

  const handleConfirmSave = useCallback(async () => {
    setConfirmSaveOpen(false);
    try {
      if (isEdit && role) {
        const request: UpdateRoleRequest = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          composite: formData.composite,
          compositeRoles: parseCompositeRoles(),
          attributes: parseAttributes(),
          statusCode: formData.statusCode || undefined,
          // All-zero GUID tells the API to clear the application ("None")
          appId: formData.appId || EMPTY_GUID,
        };
        await updateRole(role.id, request, {
          onSuccess: () => {
            notificationService.toast.success(`Role "${formData.name}" updated successfully.`);
            onSaveSuccess();
          },
        });
      } else {
        const request: CreateRoleRequest = {
          code: formData.code.trim(),
          name: formData.name.trim(),
          roleType: formData.roleType,
          appId: formData.appId || undefined,
          description: formData.description.trim() || undefined,
          composite: formData.composite,
          compositeRoles: parseCompositeRoles(),
          attributes: parseAttributes(),
          // Roles are always mirrored into Keycloak as CLIENT roles
          createInKeycloak: true,
          realmName: formData.realmName.trim() || undefined,
          clientId: formData.clientId.trim() || undefined,
        };
        await createRole(request, {
          onSuccess: () => {
            notificationService.toast.success(`Role "${formData.name}" created successfully.`);
            onSaveSuccess();
          },
        });
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const fieldMessages =
        data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)
          ? Object.values(data.errors as Record<string, string[]>).flat().join('; ')
          : '';
      notificationService.toast.error(
        data?.message || fieldMessages || err?.message ||
        `Failed to ${isEdit ? 'update' : 'create'} role. Please try again.`,
      );
    }
  }, [formData, isEdit, role, createRole, updateRole, onSaveSuccess, parseAttributes, parseCompositeRoles]);

  // ---- Minimize ----
  const handleMinimize = useCallback(() => {
    const currentCreateInstanceId = createInstanceId;
    const savedMode = isEdit ? ('edit' as const) : ('create' as const);
    const id = isEdit
      ? `${tabId || 'role'}-form-modal-edit-${role?.code || 'unknown'}`
      : `${tabId || 'role'}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    const title = formDataRef.current.name
      ? `${isEdit ? 'Edit' : 'New'} Role — ${formDataRef.current.name}`
      : isEdit
        ? 'Edit Role'
        : `New Role (${currentCreateInstanceId || '1'})`;
    const savedFormData = { ...formDataRef.current };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(savedFormData) !== savedSnapshot : false;
    minimize({
      id,
      title,
      moduleName: moduleName || 'Platform Admin',
      tabId: tabId || 'role-master',
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            savedMode,
            savedMode === 'create' ? currentCreateInstanceId : undefined,
            savedMode === 'edit' ? role?.code : undefined,
          );
        }
      },
    });
    if (onMinimize) onMinimize();
    else onClose();
  }, [tabId, moduleName, isEdit, role, minimize, onClose, onMinimize, onRestoreModal, createInstanceId]);

  // ---- Close with unsaved check ----
  const handleCloseAttempt = useCallback(() => {
    const dirty = JSON.stringify(formDataRef.current) !== initialSnapshotRef.current;
    if (dirty) setConfirmDiscardOpen(true);
    else onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const busy = saving || checkingCode;

  return (
    <>
      <div className="modal-backdrop" onClick={handleCloseAttempt} />
      <div className="modal-container modal-lg">
        {busy && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit',
          }}>
            <div style={{
              width: '2.5rem', height: '2.5rem', border: '3px solid var(--border-color)',
              borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {checkingCode ? 'Checking role code...' : isEdit ? 'Updating role...' : 'Creating role...'}
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <IconSystem.lock size={20} />
            {isEdit ? 'Edit Role' : 'Create New Role'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={busy}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleCloseAttempt} disabled={busy}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {/* Row: Code | Name */}
          <div className="form-grid-2" style={{ marginBottom: '1rem' }}>
            <div className="form-group" data-field="code" style={{ marginBottom: 0 }}>
              <label className="form-label">Role Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input ${getFieldError('code') ? 'form-field-error' : ''}`}
                value={formData.code}
                onChange={(e) => handleFieldChange('code', e.target.value)}
                placeholder="e.g. tenant_admin"
                maxLength={100}
                disabled={isEdit}
                style={isEdit ? { background: 'var(--bg-secondary)', cursor: 'not-allowed' } : undefined}
              />
              {getFieldError('code') && <div className="form-error-message">{getFieldError('code')}</div>}
              {!isEdit && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Letters, numbers, hyphens and underscores. Cannot be changed later.
                </div>
              )}
            </div>
            <div className="form-group" data-field="name" style={{ marginBottom: 0 }}>
              <label className="form-label">Role Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input ${getFieldError('name') ? 'form-field-error' : ''}`}
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder="e.g. Tenant Administrator"
                maxLength={50}
              />
              {getFieldError('name') && <div className="form-error-message">{getFieldError('name')}</div>}
            </div>
          </div>

          {/* Row: Role Type | Application */}
          <div className="form-grid-2" style={{ marginBottom: '1rem' }}>
            <div className="form-group" data-field="roleType" style={{ marginBottom: 0 }}>
              {isEdit ? (
                <>
                  <label className="form-label">Role Type</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.roleType || '—'}
                    disabled
                    style={{ background: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                  />
                </>
              ) : (
                <SyutSelect
                  label={<>Role Type <span className="text-danger">*</span></>}
                  options={roleTypeOptions}
                  value={formData.roleType}
                  onChange={(val: string | string[] | null) => handleFieldChange('roleType', (val as string) || '')}
                  placeholder="Select role type"
                  error={getFieldError('roleType')}
                  isClearable={false}
                />
              )}
            </div>
            <div className="form-group" data-field="appId" style={{ marginBottom: 0 }}>
              <SyutSelect
                label="Application"
                options={applicationOptions}
                value={formData.appId}
                // Edit mode only changes the record's application; the Keycloak
                // realm/client fields belong to create, so don't touch them here.
                onChange={(val: string | string[] | null) => (
                  isEdit
                    ? handleFieldChange('appId', (val as string) || '')
                    : handleApplicationChange((val as string) || '')
                )}
                placeholder="Select application (optional)"
                isClearable={false}
              />
            </div>
          </div>

          {/* Status — edit mode only; values come from iam.tbl_status (type = ROLE) */}
          {isEdit && (
            <div className="form-grid-2" style={{ marginBottom: '1rem' }}>
              <div className="form-group" data-field="statusCode" style={{ marginBottom: 0 }}>
                <SyutSelect
                  label="Status"
                  options={statusOptions}
                  value={formData.statusCode}
                  onChange={(val: string | string[] | null) => handleFieldChange('statusCode', (val as string) || '')}
                  placeholder={statusOptions.length ? 'Select status' : 'Loading statuses...'}
                  isClearable={false}
                  error={getFieldError('statusCode')}
                />
              </div>
              <div />
            </div>
          )}

          {/* Description */}
          <div className="form-group" data-field="description" style={{ marginBottom: '1rem' }}>
            <label className="form-label">Description</label>
            <textarea
              className={`form-input ${getFieldError('description') ? 'form-field-error' : ''}`}
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="What this role is for"
              rows={2}
              maxLength={200}
              style={{ resize: 'vertical' }}
            />
            {getFieldError('description') && <div className="form-error-message">{getFieldError('description')}</div>}
          </div>

          {/* Composite */}
          <div className="form-group" style={{ marginBottom: formData.composite ? '1rem' : '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.composite}
                onChange={(e) => handleFieldChange('composite', e.target.checked)}
              />
              Composite role (aggregates other roles)
            </label>
          </div>

          {formData.composite && (
            <div className="form-group" data-field="compositeRoles" style={{ marginBottom: '1rem' }}>
              <label className="form-label">Composite Role Codes</label>
              <input
                type="text"
                className="form-input"
                value={formData.compositeRoles}
                onChange={(e) => handleFieldChange('compositeRoles', e.target.value)}
                placeholder="Comma separated, e.g. viewer, editor"
              />
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                Codes of the roles this composite role includes.
              </div>
            </div>
          )}

          {/* Attributes */}
          <div className="form-group" data-field="attributes" style={{ marginBottom: '1rem' }}>
            <label className="form-label">Attributes (JSON)</label>
            <textarea
              className={`form-input ${getFieldError('attributes') ? 'form-field-error' : ''}`}
              value={formData.attributes}
              onChange={(e) => handleFieldChange('attributes', e.target.value)}
              placeholder='Optional JSON object, e.g. {"level": "admin"}'
              rows={2}
              style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: '0.8rem' }}
            />
            {getFieldError('attributes') && <div className="form-error-message">{getFieldError('attributes')}</div>}
          </div>

          {/* Keycloak — create mode only (the update endpoint does not sync).
              Both values are filled in from the selected application and stay editable. */}
          {!isEdit && (
            <div className="form-grid-2" style={{ marginBottom: '1rem' }}>
              <div className="form-group" data-field="realmName" style={{ marginBottom: 0 }}>
                <label className="form-label">Realm Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-input ${getFieldError('realmName') ? 'form-field-error' : ''}`}
                  value={formData.realmName}
                  onChange={(e) => handleFieldChange('realmName', e.target.value)}
                  placeholder="Select an application to fill this in"
                />
                {getFieldError('realmName') && <div className="form-error-message">{getFieldError('realmName')}</div>}
              </div>
              <div className="form-group" data-field="clientId" style={{ marginBottom: 0 }}>
                <label className="form-label">Client ID <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-input ${getFieldError('clientId') ? 'form-field-error' : ''}`}
                  value={formData.clientId}
                  onChange={(e) => handleFieldChange('clientId', e.target.value)}
                  placeholder="Select an application to fill this in"
                />
                {getFieldError('clientId') && <div className="form-error-message">{getFieldError('clientId')}</div>}
              </div>
            </div>
          )}

          {/* Read-only info — edit mode */}
          {isEdit && role && (
            <div style={{
              marginTop: '0.5rem',
              padding: '0.75rem 1rem',
              background: 'var(--bg-secondary)',
              borderRadius: '6px',
              border: '1px solid var(--border-color)',
            }}>
              <div style={{
                fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '0.5rem',
              }}>
                Role Info
              </div>
              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', fontSize: '0.8rem' }}>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>ID: </span>
                  <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: '0.75rem' }}>{role.id}</span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>Created: </span>
                  <span style={{ color: 'var(--text-primary)' }}>{new Date(role.createdAt).toLocaleString()}</span>
                </div>
                {role.updatedAt && (
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Last Updated: </span>
                    <span style={{ color: 'var(--text-primary)' }}>{new Date(role.updatedAt).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleCloseAttempt} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSaveClick} disabled={busy}>
            {isEdit ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={confirmSaveOpen}
        title={isEdit ? 'Update Role' : 'Create Role'}
        message={isEdit
          ? `Save changes to role "${formData.name}"?`
          : `Create role "${formData.name}" with code "${formData.code}"? It will also be created in Keycloak client "${formData.clientId}" (realm: ${formData.realmName}).`}
        confirmLabel={isEdit ? 'Update' : 'Create'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setConfirmSaveOpen(false)}
      />

      {/* Discard confirmation */}
      <ConfirmDialog
        open={confirmDiscardOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setConfirmDiscardOpen(false); onClose(); }}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
    </>
  );
};

export default RoleFormModal;
